import { useEffect, useState } from 'react';
import { Avatar, Box, Button, Typography } from '@mui/material';
import { Edit, History } from '@mui/icons-material';
import Moment from 'moment';
import { useQuery } from '@apollo/client/react';
import { useConfig } from '../../components/Configuration';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatDate } from '../../Formatters';
import { Change } from '../../interfaces';
import { GET_PRODUCT_CHANGES } from '../subscriptions.graphql';
import { ProductKind } from '../status';

interface Props {
  kind: ProductKind;
  subscriptionId?: number;
  /** The log refetches when this changes, since the price dialogs save without waiting. */
  revision: unknown[];
}

export function ChangesPanel({ kind, subscriptionId, revision }: Props) {
  const config = useConfig();
  const [limit, setLimit] = useState(20);
  const { data, loading, error, refetch } = useQuery<{ changes: Change[] }>(GET_PRODUCT_CHANGES, {
    variables: { kind, subscriptionId, limit: limit + 1 }, // one more than shown, to know if there are more
    pollInterval: config?.defaultPollingInterval ?? 0,
  });

  const revisionKey = JSON.stringify(revision);
  useEffect(() => {
    void refetch();
  }, [revisionKey, refetch]);

  if (!data && loading)
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <LoadingSpinner />
      </Box>
    );
  if (!data && error)
    return (
      <Typography variant="body2" color="error" sx={{ p: { xs: 2, sm: 3 } }}>
        Fikk ikke hentet endringene.
      </Typography>
    );

  const all = data?.changes ?? [];
  const changes = all.slice(0, limit);
  if (changes.length === 0)
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: { xs: 2, sm: 3 } }}>
        Ingen endringer ennå.
      </Typography>
    );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ position: 'relative', pl: 1 }}>
        <Box sx={{ position: 'absolute', left: 14, top: 8, bottom: 8, width: 2, bgcolor: 'divider' }} />
        {changes.map((c, i) => (
          <Box key={c.id} sx={{ display: 'flex', gap: 2, pb: i === changes.length - 1 ? 0 : 2.5, position: 'relative' }}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                typography: 'body2',
                bgcolor: (theme) => (c.isExternal ? theme.palette.grey[200] : theme.lighten(theme.palette.primary.main, 0.85)),
                color: c.isExternal ? 'text.secondary' : 'primary.dark',
                position: 'relative',
                zIndex: 1,
                border: 2,
                borderColor: 'background.paper',
              }}
            >
              {c.isExternal ? <History sx={{ fontSize: 'inherit' }} /> : <Edit sx={{ fontSize: 'inherit' }} />}
            </Avatar>
            <Box sx={{ flex: 1, pt: 0.25 }}>
              <Typography variant="body2">{c.description}</Typography>
              <Typography variant="caption" component="div" color="text.secondary" sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <span>{c.createdByName}</span>
                <Box component="span" sx={{ color: 'text.disabled' }}>
                  ·
                </Box>
                <span title={formatDate(c.createdAt)}>{Moment(c.createdAt).fromNow()}</span>
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {all.length > limit && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button size="small" onClick={() => setLimit(limit + 20)}>
            Last flere
          </Button>
        </Box>
      )}
    </Box>
  );
}
